import {
  clearStorageSession,
  getStorageToken,
  isStorageTokenExpired,
  persistTokenExpiration,
  setImpAdminId,
  setRegTypeItem,
  setSessionItem,
  setSessionUUIDToken,
  setStorageToken,
  setUserUUIDToken,
} from './storage';
import {
  COUNT_DAYS_SAVE_COOKIE,
  IMPERSONATE_COOKIE_FROM_CRM,
  KEY_STORAGE_TARGET_URL,
  SESSION_STORAGE_IMPERSONATE_ACTIVE,
} from '../constants';
import { getCookie, setCookie } from './cookies';
import { addDaysDate } from './functions';
import { convertToLimaTZ } from './daysjsConfig';
import { confirmSessionCode } from 'api';
import { setTargetUrlWriteLock } from './targetUrl';
import { syncSentryContext } from './sentry';
import { parseJWT } from './jwt';

export const isAlreadyAuthorized = (): boolean => {
  const token = getStorageToken();
  if (!token) {
    return false;
  }

  return !isStorageTokenExpired();
};

export const isUnderMoratorium = (moratoriumTill: any) => {
  return moratoriumTill ? convertToLimaTZ().isBefore(convertToLimaTZ(moratoriumTill)) : false;
};

export const bootstrapAuthFromUrl = async () => {
  try {
    const params = new URL(window.location.href).searchParams;
    const authParams = JSON.parse(params.get('impersonate') ?? '{}');
    const redirectCode: string | null = params.get('fb_redirect_code');

    if (getCookie(IMPERSONATE_COOKIE_FROM_CRM)) {
      setTargetUrlWriteLock();
    }

    if (redirectCode) {
      const loginByRedirectCode = async () => {
        try {
          let targetUrlToSave: string | null = null;
          const fingerprint = window.fingerPrint || {};

          if (params.has('target_url')) {
            const rawTargetUrl = params.get('target_url'); // готовый URL от Facebook — в куки как есть
            if (rawTargetUrl) {
              const countDay = addDaysDate(COUNT_DAYS_SAVE_COOKIE);
              setCookie(KEY_STORAGE_TARGET_URL, rawTargetUrl, countDay);
              targetUrlToSave = rawTargetUrl;
            }
          }

          const response: any = await confirmSessionCode({
            code: redirectCode,
            user_additional_data: { target_url: targetUrlToSave, fingerprint_js: fingerprint },
          });

          clearStorageSession();
          setStorageToken(response.jwt_token);
          persistTokenExpiration(response.jwt_token, response.jwt_token_expired_at);
          setUserUUIDToken(response.user_uuid);
          setSessionUUIDToken(response.session_uuid);
          if (targetUrlToSave) {
            setTargetUrlWriteLock();
          }
        } catch (error: any) {
          console.error(error);
        }
      };
      await loginByRedirectCode();
    }
    if (authParams.token) {
      clearStorageSession();
      setStorageToken(authParams.token);
      persistTokenExpiration(authParams.token, authParams.jwt_token_expired_at);
      const payload = parseJWT(authParams.token);
      if (payload) {
        if (payload.sub) {
          setUserUUIDToken(payload.sub);
        }
        if (payload.ses) {
          setSessionUUIDToken(payload.ses);
        }
        if (authParams.user) {
          setRegTypeItem(authParams.user.regType);
        }
      }
      if (authParams.admin_id) {
        const countDay = addDaysDate(1);
        setImpAdminId(authParams.admin_id);
        setCookie(IMPERSONATE_COOKIE_FROM_CRM, 'true', countDay);
      }
      setSessionItem(SESSION_STORAGE_IMPERSONATE_ACTIVE, '1');
    }

    // Drop sessions that already expired while sitting in localStorage (no API round-trip).
    if (getStorageToken() && isStorageTokenExpired()) {
      clearStorageSession();
    }

    syncSentryContext();
  } catch (error: any) {
    console.error(error);
  }
};

export const clearIfAnyNullAndTransform = (deps: Array<string | null | undefined>, value: string) => {
  return deps.some(item => item === null) ? '' : value.toLowerCase().replace(' ', '_');
};

export const stringIsNotANumber = (value: string) => {
  return Number.isNaN(Number(value));
};

export const textPattern = /^[a-zA-ZÁáÉéÍíÓóÚúÜüÑñ]+(( |-| )[a-zA-ZÁáÉéÍíÓóÚúÜüÑñ]+)*$/;
export const textPatternWithDigits = /^[0-9a-zA-ZÁáÉéÍíÓóÚúÜüÑñ]+(( |-| )[0-9a-zA-ZÁáÉéÍíÓóÚúÜüÑñ]+)*$/;

export const sanitizeText = (value: string, allowDigits = false): string => {
  const sanitizePattern = allowDigits ? /[^0-9a-zA-ZÁáÉéÍíÓóÚúÜüÑñ - ]/g : /[^a-zA-ZÁáÉéÍíÓóÚúÜüÑñ - ]/g;

  return value
    .replace(sanitizePattern, '')
    .replace(/^[- ]+/g, '')
    .replace(/[- ]{2,}/g, match => {
      return match[0];
    });
};

export const getSanitizedTextValue: (value: string, allowDigits?: boolean) => string = (value, allowDigits = false) => {
  const sanitizedValue = sanitizeText(value, allowDigits);
  const trimmedSanitizedValue = sanitizedValue.trim();
  const currentTextPattern = allowDigits ? textPatternWithDigits : textPattern;

  if (sanitizedValue === '' || currentTextPattern.test(trimmedSanitizedValue)) {
    return sanitizedValue;
  }

  return sanitizedValue.slice(0, -1);
};
